import { useEffect, useState } from "react";
import { detectInAppBrowser, breakoutToSystemBrowser } from "../lib/tracking-url";

/**
 * Shown only inside Instagram/Facebook/TikTok in-app webviews, where Google login,
 * cookies, and the Meta Pixel are broken. Fixed to the bottom (thumb zone) with
 * z-[60] so the fixed top navbar (z-50) can never paint over it. On Android we
 * offer a real breakout via intent://; iOS gets instructions + copy-link.
 */
const COPY: Record<string, { de: string; en: string }> = {
  instagram: {
    de: "Für Login & Anmeldung: tippe oben rechts auf ••• und wähle „Im Browser öffnen“.",
    en: "To sign up & log in: tap ••• at the top right and choose “Open in browser”.",
  },
  facebook: {
    de: "Für Login & Anmeldung: tippe oben rechts auf ••• und wähle „Im externen Browser öffnen“.",
    en: "To sign up & log in: tap ••• at the top right and choose “Open in external browser”.",
  },
  default: {
    de: "Bitte öffne diese Seite in Safari oder Chrome, um dich anzumelden.",
    en: "Please open this page in Safari or Chrome to sign up.",
  },
};

function isAndroid(): boolean {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

export default function WebviewNotice({ lang }: { lang?: "de" | "en" }) {
  const [browser, setBrowser] = useState<ReturnType<typeof detectInAppBrowser>>(null);
  const [resolvedLang, setResolvedLang] = useState<"de" | "en">(lang ?? "de");
  const [copyState, setCopyState] = useState<"idle" | "ok" | "fail">("idle");

  useEffect(() => {
    setBrowser(detectInAppBrowser());
    if (!lang && typeof window !== "undefined") {
      try {
        setResolvedLang(window.localStorage.getItem("neosearch-lang") === "en" ? "en" : "de");
      } catch {
        // storage blocked in this webview — keep the default language.
      }
    }
  }, [lang]);

  if (!browser) return null;

  const de = resolvedLang === "de";
  const msg = (COPY[browser] ?? COPY.default)[resolvedLang];

  const onOpenBrowser = () => {
    if (!breakoutToSystemBrowser(window.location.href)) onCopy();
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState("ok");
      setTimeout(() => setCopyState("idle"), 2500);
    } catch {
      // Clipboard blocked — show the URL as selectable text instead of failing silently.
      setCopyState("fail");
    }
  };

  return (
    <div
      role="alert"
      className="fixed bottom-0 inset-x-0 z-[60] w-full bg-amber-500 text-amber-950 text-[13px] leading-snug px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] flex flex-wrap items-center gap-x-3 gap-y-1.5 justify-center text-center shadow-[0_-2px_8px_rgba(0,0,0,0.15)]"
    >
      <span className="font-medium">{msg}</span>
      {isAndroid() && (
        <button
          onClick={onOpenBrowser}
          className="shrink-0 rounded-md bg-amber-950 text-amber-50 px-2.5 py-1 font-semibold"
        >
          {de ? "In Chrome öffnen" : "Open in Chrome"}
        </button>
      )}
      <button
        onClick={onCopy}
        className="shrink-0 rounded-md bg-amber-950/10 hover:bg-amber-950/20 px-2.5 py-1 font-semibold transition-colors"
      >
        {copyState === "ok"
          ? de ? "Kopiert ✓" : "Copied ✓"
          : de ? "Link kopieren" : "Copy link"}
      </button>
      {copyState === "fail" && (
        <span className="w-full select-all break-all text-[11px] font-mono bg-amber-950/10 rounded px-2 py-1">
          {typeof window !== "undefined" ? window.location.href : ""}
        </span>
      )}
    </div>
  );
}
